import { useCallback, useLayoutEffect } from 'react';

import { useSelect } from 'downshift';

import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import { checkReferenceEquality, defaultLabelGetter } from '../helpers';
import mergeRefs from '../helpers/mergeRefs';
import { DefaultItem, GenericItem, UseDropdownProps } from '../types';
import useDisabledItems, { useGetItemProps } from './useDisabledItems';

/**
 * A hook used to manage all logic needed for a Dropdown.
 */
const useDropdown = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLLabelElement
>({
  itemToString = (item) => defaultLabelGetter(item as DefaultItem | null),
  items: itemsProp,
  isItemDisabled = (item) => !!item.disabled,
  target,
  layoutDependency,
  ...props
}: UseDropdownProps<TItem, TTarget>) => {
  checkReferenceEquality({ ...props, items: itemsProp });

  const targetRef = useForwardedRef(target || null);

  const { items } = useDisabledItems({ items: itemsProp, isItemDisabled });

  const {
    isOpen: downshiftIsMenuOpen,
    getItemProps,
    selectItem,
    getMenuProps: rawGetMenuProps,
    ...useComboboxReturnValue
  } = useSelect({
    itemToString,
    items,
    ...props,
  });

  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps,
    ...useOverlayReturnValue
  } = useOverlay({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    target: targetRef,
    layoutDependency,
    ...props,
  });

  const getMenuProps: typeof rawGetMenuProps = useCallback(
    (args) => rawGetMenuProps({ ...overlayProps, ...args }),
    [overlayProps, rawGetMenuProps]
  );

  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);

  return {
    isOpen: useOverlayIsOpen,
    menuItems: items,
    getItemProps: useGetItemProps({ getItemProps }),
    getMenuProps,
    /**
     * @deprecated getMenuProps provides ref and style which overlayProps was used for
     */
    overlayProps,
    // Workaround until this PR is merged: https://github.com/downshift-js/downshift/pull/1334
    selectItem: selectItem as (item: TItem | null) => void,
    ...useComboboxReturnValue,
    targetProps: { ref: targetRef },
    ...useOverlayReturnValue,
  };
};

export default Object.assign(useDropdown, {
  stateChangeTypes: useSelect.stateChangeTypes,
});
